import React, { Fragment, useEffect, useRef, useState } from "react";
import {
  Grid,
  Box,
  Typography,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  tableCellClasses,
  CircularProgress,
} from "@mui/material";
import SelectField from "components/Select";
import { PrimaryButton } from "components/Buttons";
import Colors from "assets/Style/Colors";
import { FontFamily } from "assets";
import { ErrorToaster } from "components/Toaster";
import { useForm } from "react-hook-form";
import { CircleLoading } from "components/Loaders";
import Pagination from "components/Pagination";
import { makeStyles } from "@mui/styles";
import styled from "@emotion/styled";
import moment from "moment/moment";
import { addPermission } from "redux/slices/navigationDataSlice";
import { useDispatch } from "react-redux";
import { CommaSeparator, formatPermissionData } from "utils";
import ExportServices from "services/Export";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { PDFExport } from "@progress/kendo-react-pdf";
import DatePicker from "components/DatePicker";
import ExportAppliedVendorReport from "components/Pdf/exportApplied.vendor";
import CurrencyServices from "services/Currency";
import FinanceServices from "services/Finance";

// *For Table Style
const Row = styled(TableRow)(({ theme }) => ({
  border: 0,
}));

const Cell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    fontSize: 14,
    fontFamily: FontFamily.NunitoRegular,
    border: 0,
    padding: "15px",
    textAlign: "center",
    whiteSpace: "nowrap",
    background: Colors.primary,
    color: Colors.white,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    fontFamily: FontFamily.NunitoRegular,
    textAlign: "center",
    textWrap: "nowrap",

    padding: "5px !important",
    ".MuiBox-root": {
      display: "flex",
      gap: "6px",
      alignItems: "center",
      justifyContent: "center",
      ".MuiBox-root": {
        cursor: "pointer",
      },
    },
    svg: {
      width: "auto",
      height: "24px",
    },
    ".MuiTypography-root": {
      textTransform: "capitalize",
      fontFamily: FontFamily.NunitoRegular,
      textWrap: "nowrap",
    },
    ".MuiButtonBase-root": {
      padding: "8px",
      width: "28px",
      height: "28px",
    },
  },
}));

const useStyles = makeStyles({
  loaderWrap: {
    display: "flex",
    height: 100,
    "& svg": {
      width: "40px !important",
      height: "40px !important",
    },
  },
});

const VehicleTTReport = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const contentRef = useRef(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();

  const [loader, setLoader] = useState(false);
  const today = new Date();
  const currentDate = moment(today).format("DD-MMM-YY HH:mm");
  // *For Vendor Dropdown
  const [vendors, setVendors] = useState([]);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [vendorType, setVendorType] = useState({
    isBroker: false,
    isAgent: false,
    isPortAgent: false,
    isRecoveryAgent: false,
  });
  // *For Permissions
  const [permissions, setPermissions] = useState();

  // *For TT List
  const [data, setData] = useState();

  // *For Pagination
  const [totalCount, setTotalCount] = useState(0);
  const [pageLimit, setPageLimit] = useState(50);
  const [currentPage, setCurrentPage] = useState(1);

  // *For Filters
  const [filters, setFilters] = useState({});
  const [fromDate, setFromDate] = useState(new Date("2025-01-01"));
  const [toDate, setToDate] = useState(new Date());
  const [currencyExchangeRate, setCurrencyExchangeRate] = useState();
  const [unappliedFunds, setUnappliedFunds] = useState();
  const [vaultReceivable, setVaultReceivable] = useState();
  const [walletBalance, setWalletBalance] = useState(0);
  const [vendorUnappliedBalance, setVendorUnappliedBalance] = useState(0);

  const getCurrencies = async (currency) => {
    try {
      let params = {
        detailed: true,
      };
      const { data } = await CurrencyServices.getCurrencies(params);
      if (data?.funds?.rows?.[0]?.currency === "aed") {
        setCurrencyExchangeRate(data.currencies[0].conversion_rate);
      } else if (data?.funds?.rows?.[0]?.currency === "cad") {
        setCurrencyExchangeRate(data.currencies[1].conversion_rate);
      } else {
        setCurrencyExchangeRate(data.currencies[2].conversion_rate);
      }
    } catch (error) {
      ErrorToaster(error);
    }
  };
  // *For Handle Date
  const handleFromDate = (newDate) => {
    try {
      // eslint-disable-next-line
      if (newDate == "Invalid Date") {
        setFromDate("invalid");
        return;
      }
      setFromDate(new Date(newDate));
    } catch (error) {
      ErrorToaster(error);
    }
  };

  const handleToDate = (newDate) => {
    try {
      if (!newDate || newDate.toString() === "Invalid Date") {
        setToDate(new Date());
        return;
      }
      setToDate(newDate);
    } catch (error) {
      ErrorToaster(error);
    }
  };
  // *For Vendor
  const getVendorDropdown = async () => {
    try {
      const { data } = await ExportServices.getVendorDropdown();
      setVendors([
        ...data?.agents,
        ...data?.brokers,
        ...data?.recoveryAgents,
        ...data?.portAgents,
      ]);
    } catch (error) {
      ErrorToaster(error);
    }
  };
  const getVaultDashboard = async (page, limit, filter) => {
    setLoader(true);
    try {
      const Page = page ? page : currentPage;
      const Limit = limit ? limit : pageLimit;
      const Filter = { ...filters, ...filter };
      setCurrentPage(Page);
      setPageLimit(Limit);
      setFilters(Filter);
      let params = {
        page: Page,
        limit: Limit,
      };
      params = { ...params, ...Filter };
      const {
        data: { detail },
      } = await FinanceServices.getVehicleSumLedgerExport(params);
      const allAccounts = detail?.rows?.flatMap((row) => row.accounts || []);
      const matchedAccount = allAccounts.find(
        (account) =>
          account.type_code === "A2" &&
          account.primary_series === 20014 &&
          account.user_id === selectedVendor?.id,
      );
      setVendorUnappliedBalance(
        matchedAccount?.nature === "credit"
          ? parseFloat(
              matchedAccount?.total_credit - matchedAccount?.total_debit,
            ).toFixed(2)
          : parseFloat(
              matchedAccount?.total_debit - matchedAccount?.total_credit,
            ).toFixed(2),
      );
      setUnappliedFunds(matchedAccount);
      const vaultRecData = allAccounts.find(
        (account) =>
          account.type_code === "L2" &&
          account.primary_series === 50003 &&
          account.user_id === selectedVendor?.id,
      );

      const filteredWallet = vaultRecData;
      setVaultReceivable(filteredWallet);
      setWalletBalance(
        filteredWallet?.nature === "credit"
          ? parseFloat(
              filteredWallet?.total_credit - filteredWallet?.total_debit,
            ).toFixed(2)
          : parseFloat(
              filteredWallet?.total_debit - filteredWallet?.total_credit,
            ).toFixed(2),
      );
    } catch (error) {
      ErrorToaster(error);
    } finally {
      setLoader(false);
    }
  };
  // *For TT List
  const getTT = async (page, limit, filter) => {
    setLoader(true);
    try {
      const Page = page ? page : currentPage;
      const Limit = limit ? limit : pageLimit;
      const Filter = { ...filters, ...filter };
      setCurrentPage(Page);
      setPageLimit(Limit);
      setFilters(Filter);
      let params = {
        page: Page,
        limit: Limit,
      };

      if (fromDate && toDate) {
        params.from_date = fromDate
          ? moment(new Date(fromDate)).format("YYYY-MM-DD")
          : "";

        params.to_date = toDate
          ? moment(new Date(toDate)).format("YYYY-MM-DD")
          : "";
      }

      params = { ...params, ...Filter };
      const { data } =
        await ExportServices.getExportVendorAppliedFundReport(params);
      setTotalCount(data?.funds?.count);
      setData(data?.funds?.rows);
      const vendorType =
        data?.funds?.rows?.[0]?.vendor?.customerProfile?.type?.toLowerCase();

      const vendorTypeMap = {
        broker: { isBroker: true },
        agent: { isAgent: true },
        "port agent": { isPortAgent: true },
        "recovery agent": { isRecoveryAgent: true },
      };

      setVendorType(
        vendorTypeMap[vendorType] || {
          isBroker: false,
          isAgent: false,
          isPortAgent: false,
          isRecoveryAgent: false,
        },
      );
      getCurrencies();
      setPermissions(formatPermissionData(data?.permissions));
      data?.permissions?.forEach((e) => {
        if (e?.route && e?.identifier && e?.permitted) {
          dispatch(addPermission(e?.route));
        }
      });
    } catch (error) {
      ErrorToaster(error);
    } finally {
      setLoader(false);
    }
  };

  // *For Apply Filters
  const applyFilter = async () => {
    try {
      let data = {
        vendor_id: selectedVendor?.id,
      };
      getTT(1, "", data);
      getVaultDashboard(1, 500, { vendor_id: selectedVendor?.id });
    } catch (error) {
      ErrorToaster(error);
    }
  };

  const downloadExcel = async () => {
    const headers = tableHead.filter((item) => item !== "Action");
    const rows = data?.map((item) => [
      moment(item?.created_at).format("DD-MM-YYYY"),
      `VUAF-${item?.id ?? "-"}`,
      item?.external_no ?? "-",
      item?.fcy_amount ?? "-",
      item?.paid_amount == 0
        ? "Unpaid"
        : parseFloat(item?.paid_amount) < parseFloat(item?.fcy_amount)
          ? "Partial"
          : "Paid",
    ]);

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Sheet1");
    worksheet.addRow(headers);
    rows.forEach((r) => worksheet.addRow(r));

    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer]), "data.xlsx");
  };

  useEffect(() => {
    getVendorDropdown();
  }, []);
  const handleExportWithComponent = () => {
    if (contentRef.current) {
      contentRef.current.save();
    }
  };
  const tableHead = ["SR No."];

  // Date Column
  if (vendorType.isBroker) {
    tableHead.push("CMR Date");
  } else if (vendorType.isRecoveryAgent) {
    tableHead.push("Pickup Date");
  } else {
    tableHead.push("MF Date");
  }

  // CMR or Menifest #
  if (vendorType.isBroker) {
    tableHead.push("CMR #");
  } else if (vendorType.isAgent || vendorType.isPortAgent) {
    tableHead.push("Menifest #");
  }

  tableHead.push("Model / Make / Color");
  tableHead.push("VIN#");

  // Charges Column
  if (vendorType.isBroker) {
    tableHead.push("BR Charges");
  } else if (vendorType.isRecoveryAgent) {
    tableHead.push("Pick From");
  } else if (vendorType.isAgent) {
    tableHead.push("Agent Charges");
  } else {
    tableHead.push("In-Country Charges");
  }

  // Payable or Border
  if (vendorType.isBroker || vendorType.isPortAgent) {
    tableHead.push("Total Payable");
  } else if (vendorType.isRecoveryAgent) {
    tableHead.push("Pick To");
  } else {
    tableHead.push("Border Charges");
  }

  // Paid / Applied Amount
  if (vendorType.isBroker) {
    tableHead.push("Paid / Applied Amount");
  } else if (vendorType.isRecoveryAgent) {
    tableHead.push("Recovery Charges");
    tableHead.push("Shifting Charges");
  } else if (vendorType.isAgent) {
    tableHead.push("Total Payable");
  } else {
    tableHead.push("Paid / Applied Amount");
  }

  // Balance or other
  if (vendorType.isBroker) {
    tableHead.push("Balance");
  } else if (vendorType.isAgent) {
    tableHead.push("Paid / Applied Date");
  } else if (vendorType.isPortAgent) {
    tableHead.push("Balance");
  }

  // Dates or Past Due
  if (vendorType.isBroker) {
    tableHead.push("Paid /  Applied Date");
  } else if (vendorType.isRecoveryAgent) {
    tableHead.push("Total");
  } else if (vendorType.isAgent) {
    tableHead.push("Past Due Days");
  } else {
    tableHead.push("Paid / Applied Date");
  }

  // Extra columns for roles
  if (vendorType.isBroker || vendorType.isPortAgent) {
    tableHead.push("Past Due Days");
  }

  if (vendorType.isRecoveryAgent) {
    tableHead.push("Paid / Applied Amount");
    tableHead.push("Balance");
    tableHead.push("Paid / Applied Date");
    tableHead.push("Past Due Days");
  }

  const getDateDifference = (date1, date2) => {
    const diffTime = Math.abs(date2 - date1);
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  const calculateDueBreakup = () => {
    // Flatten all details from each data item
    const allDetails = data?.flatMap((item) => item?.details || []);

    const getDate = (item) =>
      new Date(
        vendorType.isBroker
          ? item?.vehicle?.cmr_date
          : vendorType.isRecoveryAgent
            ? item?.vehicle?.recovery?.update_recovery_date
            : item?.vehicle?.manifest_date,
      );

    const getChargeDiff = (funds = {}) => {
      if (vendorType.isBroker) {
        return (
          (parseFloat(funds?.broker_charges ?? 0) || 0) -
          (parseFloat(funds?.broker_charged_paid ?? 0) || 0)
        );
      } else if (vendorType.isPortAgent) {
        return (
          (parseFloat(funds?.country_charges ?? 0) || 0) -
          (parseFloat(funds?.country_charged_paid ?? 0) || 0)
        );
      } else if (vendorType.isRecoveryAgent) {
        return (
          (parseFloat(funds?.recovery_charges ?? 0) || 0) -
          (parseFloat(funds?.recovery_charged_paid ?? 0) || 0) +
          ((parseFloat(funds?.shifting_charges ?? 0) || 0) -
            (parseFloat(funds?.shifting_charged_paid ?? 0) || 0))
        );
      }
      return 0;
    };

    const below15Days = allDetails?.filter(
      (item) => getDateDifference(getDate(item), new Date()) <= 15,
    );

    const below16To30Days = allDetails?.filter((item) => {
      const diff = getDateDifference(getDate(item), new Date());
      return diff > 15 && diff <= 30;
    });

    const over30Days = allDetails?.filter(
      (item) => getDateDifference(getDate(item), new Date()) > 30,
    );

    const below15DaysTotal = below15Days?.reduce(
      (acc, curr) => acc + getChargeDiff(curr?.funds),
      0,
    );

    const below16To30DaysTotal = below16To30Days?.reduce(
      (acc, curr) => acc + getChargeDiff(curr?.funds),
      0,
    );

    const over30DaysTotal = over30Days?.reduce(
      (acc, curr) => acc + getChargeDiff(curr?.funds),
      0,
    );

    const total = below15DaysTotal + below16To30DaysTotal + over30DaysTotal;

    return {
      below15DaysTotal: CommaSeparator(below15DaysTotal.toFixed(2)),
      below16To30DaysTotal: CommaSeparator(below16To30DaysTotal.toFixed(2)),
      over30DaysTotal: CommaSeparator(over30DaysTotal.toFixed(2)),
      total: CommaSeparator(total.toFixed(2)),
    };
  };

  const dueBreakup = calculateDueBreakup();
  return (
    <>
      <Box sx={{ m: 4, mb: 2 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 1,
          }}
        >
          <Typography
            variant="h5"
            sx={{
              color: Colors.charcoalGrey,
              fontFamily: FontFamily.NunitoRegular,
            }}
          >
            Vendor Applied Funds Report
          </Typography>
          {data?.length > 0 && !loader && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "end",
              }}
            >
              <PrimaryButton
                title="Download PDF"
                type="button"
                style={{ backgroundColor: Colors.bluishCyan }}
                onClick={() => handleExportWithComponent(contentRef)}
              />
            </Box>
          )}
        </Box>
        <Box>
          <PDFExport
            ref={contentRef}
            paperSize={["210mm", "400mm"]}
            fileName="exportAppliedVendor"
            dpi={96}
            landscape={true}
            keepTogether=".bottom-box"
            margin={{ bottom: 80 }}
          >
            <Box className="pdf-show" display="none">
              <ExportAppliedVendorReport
                vendorFundApprovalDetail={data || []}
                currencyExchangeRate={currencyExchangeRate}
                tableHead={tableHead}
                currentDate={currentDate}
                mode="landscape"
                detailList={data || []}
                reportType={true}
                isAgent={vendorType.isAgent}
                isBroker={vendorType.isBroker}
                isRecoveryAgent={vendorType.isRecoveryAgent}
                isPortAgent={vendorType.isPortAgent}
                fromdate={fromDate}
                todate={toDate}
                dueBreakup={dueBreakup}
                unappliedFunds={unappliedFunds}
                vaultReceivable={vaultReceivable}
                walletBalance={walletBalance}
                vendorUnappliedBalance={vendorUnappliedBalance}
              />
            </Box>
          </PDFExport>
        </Box>
        <Box
          component={"form"}
          onSubmit={handleSubmit(applyFilter)}
          sx={{
            m: "20px 0 20px 5px",
            p: "20px",
            bgcolor: Colors.feta,
            border: `1px solid ${Colors.iron}`,
            borderRadius: "9px",
          }}
        >
          <Grid
            container
            spacing={1}
            alignItems={"center"}
            columns={12}
            justifyContent={"space-between"}
          >
            <Grid item xs={12} md={3}>
              <SelectField
                size="small"
                label={"Vendor"}
                options={vendors}
                selected={selectedVendor}
                onSelect={(value) => setSelectedVendor(value)}
                error={errors?.vendor?.message}
                register={register("vendor", {
                  required: "Please select vendor.",
                })}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <DatePicker
                disableFuture={true}
                size="small"
                label={"From Date"}
                value={fromDate}
                onChange={(date) => handleFromDate(date)}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <DatePicker
                disableFuture={true}
                size="small"
                // minDate={fromDate}
                label={"To Date"}
                value={toDate}
                onChange={(date) => handleToDate(date)}
                inputFormat="dd-MM-yyyy"
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <Box
                sx={{
                  mt: "11px",
                  display: "flex",
                  justifyContent: "flex-end",
                }}
              >
                <PrimaryButton title={"Search"} type={"submit"} />
              </Box>
            </Grid>
          </Grid>
        </Box>

        {loader ? (
          <CircleLoading />
        ) : (
          data && (
            <Fragment>
              {/* ========== Table ========== */}
              <TableContainer
                component={Paper}
                sx={{
                  boxShadow: "0px 8px 18px 0px #9B9B9B1A",
                  borderRadius: 2,
                  maxHeight: "calc(100vh - 330px)",
                }}
                className="table-box"
              >
                <Table stickyHeader sx={{ minWidth: 500 }}>
                  <TableHead>
                    <TableRow>
                      {tableHead.map((item, index) => (
                        <Cell className="pdf-table" key={index}>
                          {item}
                        </Cell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {!loader ? (
                      data?.length > 0 ? (
                        <Fragment>
                          {data.map((value, index) => (
                            <Fragment key={index}>
                              {value?.details?.length > 0 &&
                                value.details.map((item, dIndex) => {
                                  const vehicle = item?.vehicle;
                                  const funds = item?.funds;
                                  const agentChargesTotal = (
                                    parseFloat(funds?.agent_charged_paid || 0) +
                                    parseFloat(funds?.border_charged_paid || 0)
                                  ).toFixed(2);

                                  const totapayable = vendorType.isBroker
                                    ? CommaSeparator(
                                        parseFloat(
                                          funds?.broker_charged_paid,
                                        ).toFixed(2),
                                      )
                                    : vendorType.isAgent
                                      ? CommaSeparator(agentChargesTotal)
                                      : vendorType.isPortAgent
                                        ? CommaSeparator(
                                            funds?.country_charged_paid,
                                          )
                                        : 0;
                                  return (
                                    <Row
                                      key={index}
                                      sx={{
                                        bgcolor: index % 2 !== 0 && "#EEFBEE",
                                      }}
                                    >
                                      {/* SR No. */}
                                      <Cell>{index + 1}</Cell>

                                      {/* CMR Date / Pickup Date / MF Date */}
                                      <Cell>
                                        {vehicle
                                          ? moment
                                              .utc(
                                                vendorType.isBroker
                                                  ? vehicle.cmr_date
                                                  : vendorType.isRecoveryAgent
                                                    ? vehicle?.recovery
                                                        ?.update_recovery_date
                                                    : vehicle.manifest_date,
                                              )
                                              .format("DD-MMM-YYYY")
                                          : "-"}
                                      </Cell>

                                      {/* CMR # or Menifest # */}
                                      {(vendorType.isBroker ||
                                        vendorType.isAgent ||
                                        vendorType.isPortAgent) && (
                                        <Cell>
                                          {vendorType.isBroker
                                            ? (vehicle?.cmr_number ?? "-")
                                            : (vehicle?.manifest_number ?? "-")}
                                        </Cell>
                                      )}

                                      {/* Model / Make / Color */}
                                      <Cell>
                                        {[
                                          vehicle?.model?.name,
                                          vehicle?.make?.name,
                                          vehicle?.color,
                                        ]
                                          .filter(Boolean)
                                          .join(" / ") || "-"}
                                      </Cell>

                                      {/* VIN# */}
                                      <Cell>{vehicle?.vin ?? "-"}</Cell>

                                      {/* Charges (based on role) */}
                                      <Cell>
                                        {vendorType.isBroker
                                          ? (parseFloat(
                                              funds?.broker_charges,
                                            )?.toFixed(2) ?? "-")
                                          : vendorType.isRecoveryAgent
                                            ? (vehicle?.pickup_from ?? "-")
                                            : vendorType.isAgent
                                              ? (parseFloat(
                                                  funds?.agent_charges,
                                                )?.toFixed(2) ?? "-")
                                              : (parseFloat(
                                                  funds?.country_charges ?? 0,
                                                )?.toFixed(2) ?? "-")}
                                      </Cell>

                                      {/* Total Payable / Pick To / Border Charges */}
                                      <Cell>
                                        {vendorType.isBroker ||
                                        vendorType.isPortAgent
                                          ? (totapayable ?? "-")
                                          : vendorType.isRecoveryAgent
                                            ? (vehicle?.destination?.name ??
                                              "-")
                                            : vendorType.isAgent
                                              ? funds?.border_charges
                                                ? parseFloat(
                                                    funds?.border_charges,
                                                  )?.toFixed(2)
                                                : "-"
                                              : ("-" ?? "-")}
                                      </Cell>

                                      {/* Paid / Applied or Recovery or Total Payable */}
                                      <Cell>
                                        {vendorType.isBroker ||
                                        (!vendorType.isAgent &&
                                          !vendorType.isRecoveryAgent)
                                          ? `${
                                              parseFloat(
                                                item?.applied_amount,
                                              )?.toFixed(2) ?? "0.00"
                                            } `
                                          : vendorType.isRecoveryAgent
                                            ? (parseFloat(
                                                funds?.recovery_charges,
                                              )?.toFixed(2) ?? "-")
                                            : (totapayable ?? "-")}
                                      </Cell>
                                      {vendorType.isRecoveryAgent && (
                                        <Cell>
                                          {parseFloat(
                                            funds?.shifting_charges,
                                          )?.toFixed(2) ?? "-"}
                                        </Cell>
                                      )}

                                      {/* Balance or Shifting or Paid Date */}
                                      <Cell>
                                        {vendorType.isBroker
                                          ? (parseFloat(
                                              funds?.broker_charges -
                                                funds?.broker_charged_paid,
                                            )?.toFixed(2) ?? "-")
                                          : vendorType.isRecoveryAgent
                                            ? (parseFloat(
                                                (
                                                  (parseFloat(
                                                    funds?.recovery_charges ||
                                                      0,
                                                  ) || 0) +
                                                  (parseFloat(
                                                    funds?.shifting_charges ||
                                                      0,
                                                  ) || 0)
                                                ).toFixed(2),
                                              ) ?? "-")
                                            : vendorType.isAgent
                                              ? item?.created_at
                                                ? moment(
                                                    item?.created_at,
                                                  ).format("DD-MMM-YYYY")
                                                : "-"
                                              : (parseFloat(
                                                  funds?.country_charges ??
                                                    0 -
                                                      funds?.country_charged_paid ??
                                                    0,
                                                )?.toFixed(2) ?? "-")}
                                      </Cell>

                                      {/* Paid / Applied Date / Total / Past Due */}
                                      <Cell>
                                        {vendorType.isBroker
                                          ? item?.created_at
                                            ? moment(item?.created_at).format(
                                                "DD-MMM-YYYY",
                                              )
                                            : "-"
                                          : vendorType.isRecoveryAgent
                                            ? (parseFloat(
                                                item?.applied_amount,
                                              )?.toFixed(2) ?? "-")
                                            : vendorType.isAgent
                                              ? (moment().diff(
                                                  vehicle?.manifest_date,
                                                  "days",
                                                ) ?? "-")
                                              : item?.created_at
                                                ? moment(
                                                    item?.created_at,
                                                  ).format("DD-MMM-YYYY")
                                                : "-"}
                                      </Cell>

                                      {/* Extra column: Past Due for Broker/Port Agent */}
                                      {(vendorType.isBroker ||
                                        vendorType.isPortAgent) && (
                                        <Cell>
                                          {vendorType.isPortAgent
                                            ? moment().diff(
                                                vehicle?.manifest_date,
                                                "days",
                                              ) || "-"
                                            : vendorType.isBroker
                                              ? moment().diff(
                                                  vehicle.cmr_date,
                                                  "days",
                                                ) || "-"
                                              : "-"}
                                        </Cell>
                                      )}

                                      {/* Extra columns for Recovery Agent */}
                                      {vendorType.isRecoveryAgent && (
                                        <>
                                          <Cell>
                                            {`${
                                              parseFloat(
                                                funds?.recovery_charges -
                                                  funds?.recovery_charged_paid,
                                              )?.toFixed(2) ?? "0.00"
                                            }`}
                                          </Cell>

                                          <Cell>
                                            {item?.created_at
                                              ? moment(item?.created_at).format(
                                                  "DD-MMM-YYYY",
                                                )
                                              : "-"}
                                          </Cell>
                                          <Cell>
                                            {moment().diff(
                                              vehicle?.recovery
                                                ?.update_recovery_date,
                                              "days",
                                            ) || "-"}
                                          </Cell>
                                        </>
                                      )}
                                    </Row>
                                  );
                                })}
                            </Fragment>
                          ))}
                        </Fragment>
                      ) : (
                        <Row>
                          <Cell
                            colSpan={tableHead.length + 1}
                            align="center"
                            sx={{ fontWeight: 600 }}
                          >
                            No Data Found
                          </Cell>
                        </Row>
                      )
                    ) : (
                      <Row>
                        <Cell
                          colSpan={tableHead.length + 2}
                          align="center"
                          sx={{ fontWeight: 600 }}
                        >
                          <Box className={classes.loaderWrap}>
                            <CircularProgress />
                          </Box>
                        </Cell>
                      </Row>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              {/* ========== Pagination ========== */}
              <Pagination
                currentPage={currentPage}
                pageSize={pageLimit}
                onPageSizeChange={(size) => getTT(1, size.target.value)}
                tableCount={data?.length}
                totalCount={totalCount}
                onPageChange={(page) => getTT(page, "")}
              />
            </Fragment>
          )
        )}
      </Box>
    </>
  );
};

export default VehicleTTReport;
